import './AdsPage.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Title from '../../shared/components/title/Title';
import { PROJECTS } from '../../shared/routes/path';
import FilterPanel from '../../shared/components/filter-panel/FilterPanel';
import PromptGroupDropdown from '../../shared/components/prompt-group-dropdown/PromptGroupDropdown';
import EmptyPanel from '../../shared/components/empty-panel/EmptyPanel';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { useFilters } from '../../shared/contexts/filters/FiltersContext';
import { EffectiveFeaturesProvider } from '../../shared/contexts/effective-features/EffectiveFeaturesContext';
import { useTheme } from '../../shared/contexts/theme/ThemeContext';
import useProjectNavigate from '../../shared/hooks/projects/preview/UseProjectNavigate';
import usePromptGroupId, { ALL_PROMPT_GROUPS_ID } from '../../shared/hooks/filters/UsePromptGroupId';
import { useProjectDates } from '../projects/preview/hooks/UseProjectDates';
import useFiltersInitializing from '../../shared/hooks/filters/UseFiltersInitializing';
import { useGetProject } from '../../shared/api/hooks/project/useGetProject';
import { useGetMonitorsForProject } from '../../shared/api/hooks/monitor/useGetMonitorsForProject';
import { useGetProjectVisibilityPerMonitor } from '../../shared/api/hooks/project/stats/useGetProjectVisibilityPerMonitor';
import { usePreviousPeriodDates } from '../sources/hooks/UsePreviousPeriodDates';
import { FilterItem } from '../../shared/components/filter-box/models/FilterBoxModel';
import {
  expandSelectedChatTypes,
  getMonitorTypesForChatType,
} from '../../shared/helpers/MonitorTypeHelpers';
import { ChatType } from '../../shared/types/monitor/ChatType';
import { MonitorType } from '../../shared/types/monitor/MonitorType';
import { getFirstSeriesPalette } from '../../shared/utils/chartTheme';
import { OTHERS_COLOR } from '../../shared/utils/utils';
import { AdsFilters } from '../../shared/api/services/ads/AdsService';
import { useGetAdsSummary } from '../../shared/api/hooks/ads/useGetAdsSummary';
import { useGetAdsChart } from '../../shared/api/hooks/ads/useGetAdsChart';
import { useGetAdsAdvertisers } from '../../shared/api/hooks/ads/useGetAdsAdvertisers';
import AdsTiles from './components/AdsTiles';
import AdsAdvertiserSelect from './components/AdsAdvertiserSelect';
import AdPresenceSection from './components/AdPresenceSection';
import AdShareTable from './components/AdShareTable';
import AdsBreakdownSection from './components/AdsBreakdownSection';
import AdsPromptsSection from './components/AdsPromptsSection';
import AdsReportingPausedNotice from './components/AdsReportingPausedNotice';
import { AdPreviewProvider, AdsTooltipProvider } from './components/AdsTableParts';
import { ADS_CAPABLE_MONITOR_TYPES } from './helpers/AdsHelper';
import useIsMobile from '../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../shared/components/common/mobile-menu/MobilePageHeader';

const AdsPage: React.FC = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const location = useLocation();
  const projectIdNumber = Number(projectId);
  const { accountResponse } = useAccount();
  const [searchParams] = useSearchParams();
  const { promptGroupId } = usePromptGroupId();
  const { monitorPrompts } = useGetMonitorsForProject(projectIdNumber);
  const { project } = useGetProject(projectIdNumber);
  // Same gate as the sidebar entry: the LOGGED-IN account's addon is always required; the super-admin
  // role only widens the reach to other accounts' projects, it unlocks nothing by itself.
  const hasAdsAddon = !!accountResponse?.addons?.includes('ADS_STATS');
  // The theme context makes the advertiser palette recompute on a light/dark switch.
  const { theme } = useTheme();
  const isMobile = useIsMobile();

  // Normalized advertiser domains; empty = all advertisers (no filter).
  const [selectedAdvertisers, setSelectedAdvertisers] = useState<string[]>([]);

  const { selectedChatType, selectedChatTypes, monitorPeriod } = useFilters();
  const { startDate, endDate } = useProjectDates();

  const filteredMonitorPrompts = useMemo(() => {
    if (!monitorPrompts) return null;
    if (promptGroupId === ALL_PROMPT_GROUPS_ID) return monitorPrompts;
    return monitorPrompts.filter((prompt) => prompt.promptGroupId === promptGroupId);
  }, [monitorPrompts, promptGroupId]);

  const filteredMonitorPromptIds = useMemo(() => {
    if (!monitorPrompts || promptGroupId === ALL_PROMPT_GROUPS_ID) return undefined;
    return (filteredMonitorPrompts ?? [])
      .map((prompt) => prompt.id)
      .filter((id): id is number => id != null);
  }, [monitorPrompts, promptGroupId, filteredMonitorPrompts]);

  // FiltersContext parses `?models=` against the ACCOUNT's model list (the provider wraps the page
  // from outside), so a non-ads model such as `gemini` can land here. Narrow to the ads-capable
  // types and treat an empty intersection as "no filter" (the tab has no model dropdown any more —
  // ChatGPT only — but a legacy `?models=` link must not blank the page).
  const hasExplicitModelFilter = searchParams.get('models') !== null;
  const filterMonitorTypes = useMemo(() => {
    if (!hasExplicitModelFilter || !selectedChatTypes || selectedChatTypes.length === 0)
      return undefined;
    const expandedChatTypes = expandSelectedChatTypes(selectedChatTypes);
    const adsTypes = Array.from(expandedChatTypes)
      .flatMap((ct) => getMonitorTypesForChatType(ct as ChatType))
      .filter((type) => ADS_CAPABLE_MONITOR_TYPES.includes(type));
    return adsTypes.length > 0 ? adsTypes : undefined;
  }, [hasExplicitModelFilter, selectedChatTypes]);

  const adsFilters: AdsFilters = useMemo(
    () => ({
      models: filterMonitorTypes,
      promptIds: filteredMonitorPromptIds,
      advertisers: selectedAdvertisers.length > 0 ? selectedAdvertisers : undefined,
    }),
    [filterMonitorTypes, filteredMonitorPromptIds, selectedAdvertisers]
  );
  // The advertisers list stays advertiser-unfiltered — it feeds the filter dropdown and keeps shares global.
  const advertiserlessFilters = useMemo(
    () => ({ models: filterMonitorTypes, promptIds: filteredMonitorPromptIds }),
    [filterMonitorTypes, filteredMonitorPromptIds]
  );

  // Owner features narrowed to the ads-capable family (ChatGPT) — drives EffectiveFeaturesProvider
  // and the "monitoring not enabled" state.
  const adsFeatures = useMemo(() => {
    const ownerFeatures = (project?.ownerFeatures ?? accountResponse?.features ?? []) as MonitorType[];
    return ownerFeatures.filter((feature) => ADS_CAPABLE_MONITOR_TYPES.includes(feature));
  }, [project?.ownerFeatures, accountResponse?.features]);
  const adsMonitoringAvailable = adsFeatures.length > 0;

  const { data: summary, isLoading: isSummaryLoading } = useGetAdsSummary(
    projectIdNumber,
    startDate,
    endDate,
    adsFilters,
    adsMonitoringAvailable
  );
  // Preceding period of equal length — the tiles' trend badges (same comparison as Sources).
  const previousPeriod = usePreviousPeriodDates(startDate, endDate);
  const { data: previousSummary } = useGetAdsSummary(
    projectIdNumber,
    previousPeriod?.previousStartDate ?? null,
    previousPeriod?.previousEndDate ?? null,
    adsFilters,
    adsMonitoringAvailable
  );
  const {
    data: chart,
    isLoading: isChartLoading,
    isError: isChartError,
    refetch: refetchChart,
  } = useGetAdsChart(
    projectIdNumber,
    monitorPeriod,
    startDate,
    endDate,
    adsFilters,
    adsMonitoringAvailable
  );
  const {
    advertisers,
    isLoading: isAdvertisersLoading,
    isError: isAdvertisersError,
    refetch: refetchAdvertisers,
  } = useGetAdsAdvertisers(
    projectIdNumber,
    startDate,
    endDate,
    advertiserlessFilters,
    adsMonitoringAvailable
  );

  const visibilityChatTypes = useMemo(
    () =>
      hasExplicitModelFilter && selectedChatTypes.length > 0
        ? Array.from(expandSelectedChatTypes(selectedChatTypes))
        : undefined,
    [hasExplicitModelFilter, selectedChatTypes]
  );
  const { visibilityPerMonitor } = useGetProjectVisibilityPerMonitor(
    projectIdNumber,
    filteredMonitorPrompts,
    selectedChatType,
    startDate,
    endDate,
    visibilityChatTypes
  );
  const visibilityByMonitor = useMemo(
    () => new Map(visibilityPerMonitor.map((row) => [row.monitorId, row.visibility])),
    [visibilityPerMonitor]
  );

  const filtersInitializing = useFiltersInitializing();

  // One stable advertiser → color mapping (by global rank) shared by the stacked chart and the ranking.
  const advertiserColorMap = useMemo(() => {
    const palette = getFirstSeriesPalette();
    const map = new Map<string, string>();
    advertisers.forEach((advertiser, index) => {
      map.set(advertiser.domain, palette[index % palette.length]);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advertisers, theme]);
  const colorForAdvertiser = useCallback(
    (domain: string) => advertiserColorMap.get(domain) ?? OTHERS_COLOR,
    [advertiserColorMap]
  );

  const displayAdvertisers = useMemo(
    () =>
      selectedAdvertisers.length > 0
        ? advertisers.filter((advertiser) => selectedAdvertisers.includes(advertiser.domain))
        : advertisers,
    [advertisers, selectedAdvertisers]
  );

  // A date-range/model/group change can drop selected advertisers from the list; without this reset
  // the filter would keep zeroing data for advertisers the dropdown no longer shows.
  useEffect(() => {
    if (selectedAdvertisers.length === 0 || isAdvertisersLoading) {
      return;
    }
    const known = selectedAdvertisers.filter((domain) =>
      advertisers.some((advertiser) => advertiser.domain === domain)
    );
    if (known.length !== selectedAdvertisers.length) {
      setSelectedAdvertisers(known);
    }
  }, [selectedAdvertisers, isAdvertisersLoading, advertisers]);

  // Daily / Weekly / Monthly live in the chart header (like the Sources trends chart), not in the
  // top filter bar — the granularity only affects the chart. Links keep the `?models=` query.
  const periodFilterItems: FilterItem[] = useMemo(() => {
    if (!monitorPeriod) {
      return [];
    }
    const baseParts = location.pathname.split('/').filter(Boolean).slice(0, -2);
    const urlFor = (period: string) => `/${baseParts.join('/')}/${period}/all${location.search}`;
    return [
      { url: urlFor('daily'), label: t('dailyFilter'), isActive: monitorPeriod === 'DAILY' },
      { url: urlFor('weekly'), label: t('weeklyFilter'), isActive: monitorPeriod === 'WEEKLY' },
      { url: urlFor('monthly'), label: t('monthlyFilter'), isActive: monitorPeriod === 'MONTHLY' },
    ];
  }, [location.pathname, location.search, monitorPeriod, t]);

  useProjectNavigate(projectIdNumber);

  if (!accountResponse || !projectId || !hasAdsAddon) {
    return <Navigate to={PROJECTS} replace />;
  }

  // Telefon: kalendarz w górnym pasku (osobny `FilterPanel` z samym zakresem dat — stan zakresu jest
  // we `FiltersContext`, więc oba panele pokazują to samo), pozostałe filtry pod paskiem.
  const filters = (
    <FilterPanel
      baseUrl={location.pathname}
      showDateRange={!isMobile}
      showChatTypeDropdown={false}
      showPeriodTabs={false}
    >
      <PromptGroupDropdown projectId={projectIdNumber} />
      {/* No model filter: the tab analyses ChatGPT only. */}
      <AdsAdvertiserSelect
        advertisers={advertisers}
        selected={selectedAdvertisers}
        onChange={setSelectedAdvertisers}
      />
    </FilterPanel>
  );

  // The model filter must list the project OWNER's models (narrowed to the ads-capable families) —
  // same pattern as Context analysis: a superadmin viewing a client's project must not pick from
  // their own account's model list.
  return (
    <EffectiveFeaturesProvider
      features={adsFeatures}
      addons={project?.ownerAddons ?? accountResponse.addons ?? []}
    >
      <AdPreviewProvider>
      <AdsTooltipProvider>
      <div className="ads-page page-content">
        <div className="page-box">
          {/* Telefon: tytuł i kalendarz w górnym pasku, filtry pod nim (jak na Źródłach). */}
          <MobilePageHeader
            title={<span>{t('ads.title')}</span>}
            actions={
              <FilterPanel
                baseUrl={location.pathname}
                showChatTypeDropdown={false}
                showPeriodTabs={false}
              />
            }
          />
          {isMobile ? (
            <div className="mobile-page-filters">{filters}</div>
          ) : (
          <div className="page-box__filters" data-observe-sticky>
            <Title
              isBaseCategory={false}
              title={t('ads.title')}
              children={<div className="filters">{filters}</div>}
              showStatusDot={true}
              childrenOnMain={<div className="title-box__subtitle">{t('ads.desc')}</div>}
            />
          </div>
          )}
          <div className="page-box__stats">
            {!adsMonitoringAvailable ? (
              <div className="ads-page__not-enabled">
                <EmptyPanel textKey="ads.notEnabledTitle" descriptionKey="ads.notEnabledDesc" />
              </div>
            ) : (
              <>
                <AdsReportingPausedNotice />
                <AdsTiles
                  summary={summary}
                  previousSummary={previousSummary}
                  isLoading={isSummaryLoading || filtersInitializing}
                />
                <div className="ads-page__overview">
                  <AdPresenceSection
                    chart={chart}
                    isLoading={isChartLoading || filtersInitializing}
                    isError={isChartError}
                    onRetry={() => void refetchChart()}
                    period={monitorPeriod || 'DAILY'}
                    startDate={startDate}
                    endDate={endDate}
                    periodFilterItems={periodFilterItems}
                    colorForAdvertiser={colorForAdvertiser}
                  />
                  <AdShareTable
                    advertisers={displayAdvertisers}
                    isLoading={isAdvertisersLoading || filtersInitializing}
                    isError={isAdvertisersError}
                    onRetry={() => void refetchAdvertisers()}
                    colorForAdvertiser={colorForAdvertiser}
                  />
                </div>
                <div className="stats stats--full-width">
                  <AdsBreakdownSection
                    projectId={projectIdNumber}
                    startDate={startDate}
                    endDate={endDate}
                    filters={adsFilters}
                    advertisers={advertisers}
                    isAdvertisersLoading={isAdvertisersLoading || filtersInitializing}
                    isAdvertisersError={isAdvertisersError}
                    onRetryAdvertisers={() => void refetchAdvertisers()}
                  />
                </div>
                <div className="stats stats--full-width">
                  <AdsPromptsSection
                    projectId={projectIdNumber}
                    startDate={startDate}
                    endDate={endDate}
                    filters={adsFilters}
                    advertisers={advertisers}
                    visibilityByMonitor={visibilityByMonitor}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      </AdsTooltipProvider>
      </AdPreviewProvider>
    </EffectiveFeaturesProvider>
  );
};

export default AdsPage;
